import { useEffect, useRef } from 'react';
import { LevelMeter } from './LevelMeter';

// Setup-Self-Check: öffnet das gewählte Audio-Input-Gerät und zeigt seinen Pegel über den
// (Clipping-)LevelMeter — so sieht man sofort, ob auf dem gewählten Gerät überhaupt Signal ankommt.
// Gibt das Gerät bei Geräte-Wechsel/Unmount wieder frei. Nur während des Setups mounten (nicht im Live-Betrieb).
export function InputDeviceMeter({ deviceId }: { deviceId: string }) {
  const analyserRef = useRef<AnalyserNode | null>(null);
  // ArrayBuffer-backed: getFloatTimeDomainData verlangt (TS 5.7+) Float32Array<ArrayBuffer>.
  const bufRef = useRef(new Float32Array(new ArrayBuffer(2048 * 4)));
  const i16Ref = useRef(new Int16Array(2048));

  useEffect(() => {
    let ctx: AudioContext | null = null;
    let stream: MediaStream | null = null;
    let cancelled = false;
    navigator.mediaDevices.getUserMedia({
      audio: { deviceId: deviceId ? { exact: deviceId } : undefined, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    }).then((s) => {
      if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
      stream = s;
      ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(s);
      const an = ctx.createAnalyser();
      an.fftSize = 2048;
      src.connect(an);
      analyserRef.current = an;
    }).catch(() => { /* kein Zugriff / Gerät weg → Meter bleibt leer */ });
    return () => {
      cancelled = true;
      analyserRef.current = null;
      stream?.getTracks().forEach((t) => t.stop());
      void ctx?.close().catch(() => {});
    };
  }, [deviceId]);

  // Liest pro Frame frische Zeitbereichsdaten aus dem Analyser und reicht sie als Int16 an den LevelMeter.
  const provider = (): Int16Array | null => {
    const an = analyserRef.current;
    if (!an) return null;
    const buf = bufRef.current;
    an.getFloatTimeDomainData(buf);
    const i16 = i16Ref.current;
    for (let i = 0; i < buf.length; i++) i16[i] = Math.max(-32768, Math.min(32767, Math.round(buf[i] * 32767)));
    return i16;
  };

  return <LevelMeter samplesProvider={provider} />;
}
